import { useCallback, type ReactElement, type ReactNode } from "react";
import { Router } from "wouter";
import { type ClaimResult, useAroundNavBoundary } from "../navigation/useAroundNavBoundary.ts";
import { canvasContentForPath } from "./canvasContent.ts";
import { useCanvasActionContext } from "./canvasContext.tsx";

type CanvasNavBoundaryProps = {
  children: ReactNode;
};

/**
 * Navigation boundary for a canvas: maps an entity path to a canvas token
 * ({@link canvasContentForPath}) and drills it onto the stack in place, so Back
 * walks out the way the user came in. Non-entity paths, no canvas, or a URN this
 * canvas can't render delegate outward (→ sidebar → real nav).
 *
 * One behavior at every depth, deliberately. This used to take a `mode`: the
 * boundary around the conversation *reset* the stack while the one inside each
 * card *pushed*, which meant the same link drilled or discarded the trail
 * depending on which side of the card frame it was rendered on. A link is a link
 * — where it happens to sit is not something the reader can see, and it is not a
 * reason for Back to work in one place and not the other.
 *
 * A duplicate push is swallowed rather than delegated: the entity asked for is
 * already the top card, so handing the click outward would navigate the page
 * away from the card that is answering it.
 *
 * A max-depth push is delegated, because it is not the same thing. Nothing was
 * added *and* the target is not what is showing, so swallowing would leave the
 * user on an unrelated card with no sign their click was refused. Falling
 * outward reaches the sidebar and then real navigation, which at least opens
 * what they asked for. The depth ceiling is a backstop against a corrupt
 * persisted stack rather than a limit a real drill-down reaches, so this path is
 * about staying honest when it does, not about it being common.
 */
export function CanvasNavBoundary({ children }: CanvasNavBoundaryProps): ReactElement {
  const canvas = useCanvasActionContext();

  /**
   * Maps a canonical path to a canvas action and reports whether navigation was
   * presented, intentionally swallowed, or delegated to the enclosing boundary.
   */
  const claim = useCallback(
    (to: string): ClaimResult => {
      if (!canvas) return "delegate"; // no canvas (mobile / gate-off) → sidebar / page
      const urn = canvasContentForPath(to);
      if (!urn) return "delegate"; // not a canvas entity → hand outward
      switch (canvas.push(urn)) {
        case "pushed":
          return "claimed";
        case "duplicate":
          return "swallow";
        case "max-depth":
        case "unsupported":
          return "delegate";
      }
    },
    [canvas],
  );

  const aroundNav = useAroundNavBoundary(claim);
  return <Router aroundNav={aroundNav}>{children}</Router>;
}
